import classNames from 'classnames';
import React, { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import { DISABLED_CONTROL } from 'components/Button/Button';

/*
 * The portalled surface for a useMenu() popover. Owns the portal, the fixed positioning,
 * the contentRef wiring and the panel chrome, so call sites don't repeat all four.
 * Renders nothing until the menu is open AND positioned (see useMenu).
 */
export const MenuPanel = ({ menu, className, children }) => {
  const isPositioned = !!menu.position;
  useLayoutEffect(() => {
    if (menu.isOpen && isPositioned) menu.contentRef.current?.querySelector('button:not([disabled])')?.focus();
  }, [menu.isOpen, isPositioned, menu.contentRef]);
  if (!menu.isOpen || !menu.position) return null;

  return createPortal(
    <div
      ref={menu.contentRef}
      style={{ position: 'fixed', ...menu.position }}
      onBlur={(e) => {
        // Not `e.relatedTarget` directly: touch-driven focus changes (tapping a menu item on
        // a phone) commonly report it as null even though focus is landing on another button
        // inside this same panel — WebKit in particular. Closing synchronously on that false
        // signal unmounts the panel before the tap's click event fires, so the tap silently
        // does nothing instead of running the item's action. Deferring one frame lets
        // document.activeElement settle first.
        const panel = e.currentTarget;
        requestAnimationFrame(() => {
          if (!panel.contains(document.activeElement)) menu.close();
        });
      }}
      onKeyDown={(e) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
        const items = [...e.currentTarget.querySelectorAll('button:not([disabled])')];
        if (!items.length) return;
        e.preventDefault();
        const index = items.indexOf(document.activeElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next].focus();
      }}
      className={classNames('z-[70] flex flex-col overflow-y-auto rounded-chip border border-ui-hair-3 bg-ui-overlay p-2 shadow-menu', className)}
    >
      {children}
    </div>,
    document.body,
  );
};

/* Section label inside a menu — same recipe as the .ui-label utility. */
export const MenuLabel = ({ children }) => (
  <div className="ui-label px-2.5 pb-2 pt-1">{children}</div>
);

/* A menu row. `tone="danger"` for destructive actions. */
export const MenuItem = ({ tone = 'default', icon, onClick, className, children, ...props }) => (
  <button
    {...props}
    type="button"
    onClick={onClick}
    className={classNames(
      'flex min-h-[44px] flex-none items-center gap-2.5 rounded-row px-2.5 py-2.5 text-left text-[13.5px] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
      DISABLED_CONTROL,
      tone === 'danger'
        ? 'text-ui-danger hover:bg-ui-danger-wash'
        : 'text-ui-2 hover:bg-ui-border hover:text-ui',
      className,
    )}
  >
    {icon}
    {children}
  </button>
);

export const MenuDivider = () => <span className="my-1 mx-2 h-px bg-ui-hair-2" />;
